import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, webPageSchema } from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";

export default function Privacy() {
  const page = webPageSchema({
    path: "/privacy",
    title: "Privacy Policy for shariqrefai.com | Shariq Refai, MD",
    description:
      "Privacy policy for shariqrefai.com. How we handle the limited information collected through this educational author and media platform, and your choices.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Privacy Policy", path: "/privacy" },
    ],
  });
  return (
    <SiteLayout>
      <SEO
        title="Privacy Policy for shariqrefai.com | Shariq Refai, MD"
        description="Privacy policy for shariqrefai.com. How we handle the limited information collected through this educational author and media platform, and your choices."
        url="/privacy"
        schema={[
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Privacy", path: "/privacy" },
          ]),
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Privacy Policy</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            Privacy Policy
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            How information is handled on this website.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Last updated: July 16, 2026.</strong>
          </p>

          <p>
            This Privacy Policy describes how shariqrefai.com (the "website")
            handles information when you visit or interact with the site.
            This website is an educational author and media platform. It is
            not a clinical service and does not collect or store protected
            health information (PHI) or operate as a HIPAA-covered service.
            Clinical care, when relevant, is provided through{" "}
            <a href={withNetworkUtm("https://shrinkmd.com/", "privacy_page")} target="_blank" rel="noopener noreferrer">
              shrinkMD
            </a>{" "}
            and is governed by shrinkMD's own privacy policies and HIPAA
            obligations.
          </p>
          <p>
            Although this website is not a HIPAA-covered service, applicable
            U.S. state laws (including the Florida Information Protection
            Act, Fla. Stat. §501.171) may govern the limited information
            collected. Reasonable security measures are maintained, and any
            qualifying breach of personal information will be handled in
            accordance with applicable state notification requirements.
          </p>

          <h2>Not for Protected Health Information</h2>
          <p>
            This website is not intended for transmission of protected health
            information and is not a platform for clinical care
            communications.
          </p>
          <p>
            Do not submit medical records, psychiatric symptoms, medication
            information, emergency details, or other confidential health
            information through this website.
          </p>
          <p>
            shariqrefai.com is separate from shrinkMD's clinical platform.
            This website is not monitored for clinical or crisis
            communications.
          </p>

          <h2>Information We Collect</h2>
          <h3>Information you provide</h3>
          <p>
            If you use the contact form, we collect the information you
            choose to submit, such as your name, email address, inquiry type,
            and the contents of your message. This information is used to
            respond to your inquiry. Please do not submit any personal
            health information through this form.
          </p>

          <h3>Information collected automatically</h3>
          <p>
            Like most websites, this site may automatically collect limited
            technical information when you visit, including IP address,
            browser type, device type, referring URL, pages visited, and
            timestamps. This information is used to operate, secure, and
            improve the site.
          </p>

          <h3>Cookies and similar technologies</h3>
          <p>
            This website uses <strong>Google Analytics 4</strong> (measurement
            ID <code>G-P568VWDRRY</code>) to understand aggregate traffic. GA4
            is loaded with Google Consent Mode v2 in strict mode: no analytics
            cookies are set and no analytics requests are sent to Google until
            you actively accept on the consent banner shown on your first
            visit. If you decline, remain undecided, or send a Global Privacy
            Control or Do Not Track signal, analytics stays off. See the{" "}
            <Link href="/cookies">Cookies &amp; Analytics</Link> page for the
            specific cookies, retention, and how to change or withdraw
            consent.
          </p>
          <p>
            No advertising cookies, retargeting pixels, or profile-building
            trackers are used on this website. Advertising features and
            Google Signals are disabled on the GA4 property.
          </p>

          <h2>How We Use Information</h2>
          <ul>
            <li>To respond to inquiries you submit through the contact form.</li>
            <li>To operate, maintain, and secure the website.</li>
            <li>To understand, in aggregate, how the website is used so it can be improved.</li>
            <li>To comply with applicable legal obligations.</li>
          </ul>

          <h2>How We Share Information</h2>
          <p>
            We do not sell your personal information. Limited information may
            be shared with service providers that help operate the website
            (including Cloudflare for hosting and content delivery, and, only
            after you consent, Google LLC as the analytics processor for
            Google Analytics 4), and only to the extent necessary to provide
            those services. We may also disclose information when required by
            law, to protect rights and safety, or in connection with a
            business transfer.
          </p>
          <p>
            When you accept analytics, Google Analytics 4 processes truncated
            IP addresses, a randomly assigned visitor ID, and page and event
            data. Processing is governed by Google's{" "}
            <a
              href="https://policies.google.com/technologies/partner-sites"
              target="_blank"
              rel="noopener noreferrer"
            >
              Analytics data processing terms
            </a>
            . Event-level and user-level data is retained by Google for up to
            14 months and then automatically deleted.
          </p>

          <h2>Third-Party Services and Links</h2>
          <p>
            The website may link to third-party websites and services,
            including shrinkMD, shrinQ, Unstuck, LinkedIn, Apple, and external
            publishers. Those services have their own privacy policies, and
            this Privacy Policy does not apply to them.
          </p>
          <p>
            The website also loads Google Fonts from Google's servers on every
            visit (see Third-Party Fonts below) and, only after you consent,
            Google Analytics 4 from googletagmanager.com. When you decline or
            remain undecided, no analytics scripts are loaded.
          </p>

          <h2>Third-Party Fonts</h2>
          <p>
            This website loads typography from Google Fonts, a third-party
            service. When fonts are loaded, your IP address is transmitted
            to Google. No personally identifying information beyond IP
            address is shared in this transaction. You can read Google's
            privacy policy at{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              policies.google.com/privacy
            </a>
            .
          </p>

          <h2>Children</h2>
          <p>
            The website is not directed to children, and content is intended
            for adult audiences (18 and older). We do not knowingly collect
            personal information from children under 16. If you believe a
            child has provided personal information through the website,
            please contact us at{" "}
            <a href="mailto:privacy@shariqrefai.com">privacy@shariqrefai.com</a>{" "}
            so it can be promptly deleted.
          </p>

          <h2>Your U.S. State Privacy Rights</h2>
          <p>
            Depending on your state of residence, you may have one or more
            of the following rights regarding personal information collected
            through this website: the right to know or access the personal
            information collected about you; the right to correct inaccurate
            personal information; the right to delete personal information;
            the right to data portability; the right to opt out of the sale
            or sharing of personal information for cross-context behavioral
            advertising; the right to limit the use and disclosure of
            sensitive personal information; and the right not to be subject
            to discrimination for exercising these rights.
          </p>

          <h3>No Sale or Sharing</h3>
          <p>
            Shariq Refai, MD, MBA does not sell or share (as those terms are
            defined in California Civil Code §1798.140) personal information
            collected through this website and has not done so in the
            preceding twelve (12) months. Shariq Refai, MD, MBA does not
            knowingly collect or sell the personal information of consumers
            under the age of 16.
          </p>

          <h3>California Shine the Light</h3>
          <p>
            California residents may request information regarding any
            disclosure of personal information to third parties for their
            direct marketing purposes by emailing{" "}
            <a href="mailto:privacy@shariqrefai.com">privacy@shariqrefai.com</a>.
          </p>

          <h3>Submitting a Request</h3>
          <p>
            To exercise any right, email{" "}
            <a href="mailto:privacy@shariqrefai.com">privacy@shariqrefai.com</a>{" "}
            with the subject line "Privacy Rights Request" and identify the
            right you are exercising and the state in which you reside.
            Requests will be verified and responded to within the timeframe
            required by applicable law (generally 45 days, extendable in
            limited circumstances). You may designate an authorized agent to
            act on your behalf.
          </p>

          <h2>EEA, UK, and Swiss Visitors</h2>
          <p>
            If you are visiting this website from the European Economic
            Area, the United Kingdom, or Switzerland, you may have rights
            under the EU/UK GDPR and the Swiss FADP, including the right to
            access, rectify, erase, restrict, or object to the processing of
            your personal data, the right to data portability, the right to
            withdraw consent where processing is based on consent, and the
            right to lodge a complaint with your supervisory authority. The
            lawful basis for processing the limited information collected
            through this website is our legitimate interest in operating and
            securing the website and in responding to inquiries you choose
            to submit, and your consent where required.
          </p>
          <p>
            This website is operated from the United States, and any
            information you submit, including contact-form data and
            automatically collected technical information, will be
            transferred to, processed on, and stored on servers located in
            the United States. The United States may not provide the same
            level of data protection as your home jurisdiction. By using
            this website or submitting information through it, you
            acknowledge that transfer. To exercise any right, email{" "}
            <a href="mailto:privacy@shariqrefai.com">privacy@shariqrefai.com</a>.
          </p>

          <h2>Data Retention</h2>
          <p>
            Contact-form submissions are retained for the period necessary
            to respond to and resolve your inquiry, and for an additional
            twenty-four (24) months thereafter for business-record purposes,
            after which they are deleted or de-identified. Automatically
            collected technical and log information is retained for up to
            thirteen (13) months. Information may be retained longer where
            required by law or to resolve a dispute or enforce an agreement.
          </p>

          <h2>Breach Notification</h2>
          <p>
            In the event of a security incident affecting personal
            information, affected individuals and applicable regulators will
            be notified consistent with applicable U.S. state and federal
            breach-notification law.
          </p>

          <h2>Data Security</h2>
          <p>
            Reasonable administrative, technical, and physical safeguards
            are used to protect the information collected; however, no
            internet transmission or storage system is completely secure.
          </p>

          <h2>Changes to This Policy</h2>
          <p>
            This Privacy Policy may be updated from time to time. The updated
            version will be posted on this page with a revised "Last updated"
            date.
          </p>

          <h2>Contact</h2>
          <p>
            For privacy questions, email{" "}
            <a href="mailto:privacy@shariqrefai.com">privacy@shariqrefai.com</a>{" "}
            or use the <Link href="/contact">contact page</Link>.
          </p>
        </div>
      </article>
    </SiteLayout>
  );
}
